import { label, timestamp } from "./format";
import type { ExecutionCheck } from "./types";

const statuses: Record<ExecutionCheck["status"], string> = {
  running: "Execution running",
  queued: "Registered, awaiting execution",
  finished: "Execution finished; delivery is checked separately",
  absent: "No execution found yet",
  unavailable: "Execution check unavailable",
  unsupported: "Execution heartbeat unavailable for this path",
  owner_changed: "Execution owner changed during the check",
};

export function ExecutionChecks({
  checks,
  compact = false,
}: {
  checks?: Record<string, ExecutionCheck>;
  compact?: boolean;
}) {
  if (!checks || !Object.keys(checks).length) return null;
  return (
    <div
      className={
        compact
          ? "w-full text-xs text-muted-foreground"
          : "rounded border p-3 text-sm"
      }
    >
      {!compact && <p className="mb-2 font-medium">Execution checks</p>}
      {Object.entries(checks).map(([source, check]) => (
        <div key={source} className={compact ? undefined : "mb-2 last:mb-0"}>
          <p>
            {check.stale !== false ? "Stale check: " : ""}
            {statuses[check.status] ?? "Unknown execution check"}
          </p>
          {!compact && (
            <>
              <p className="text-muted-foreground">
                {label(source)} · Checked {timestamp(check.checked_at)}
              </p>
              {check.worker_status && (
                <p>Worker status: {label(check.worker_status.toLowerCase())}</p>
              )}
              {check.task_status && (
                <p>Container task: {label(check.task_status.toLowerCase())}</p>
              )}
              {check.heartbeat_at && (
                <p>Worker heartbeat: {timestamp(check.heartbeat_at)}</p>
              )}
              {check.lease_expires_at && (
                <p>
                  Registered lease deadline: {timestamp(check.lease_expires_at)}
                </p>
              )}
              {check.turn_sequence !== undefined && (
                <p>Turn sequence: {check.turn_sequence}</p>
              )}
            </>
          )}
        </div>
      ))}
      {!compact && (
        <p className="text-xs text-muted-foreground">
          Point-in-time execution evidence can lag changes. A heartbeat shows
          worker activity; stage progress and final delivery require their own
          evidence.
        </p>
      )}
    </div>
  );
}
